import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, ExternalLink, X, Upload, BadgeCheck } from 'lucide-react';
import api, { mediaUrl, uploadImage, errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STATUS_STYLE = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  inactive: 'bg-red-100 text-red-700',
};

const BLANK = {
  name: '', role: '', photo_url: '', short_bio: '', full_bio: '', specialties: [],
  years_experience: '', gender: 'male', experience_level: 'associate', availability: 'full_time',
  social: { facebook: '', instagram: '', linkedin: '', website: '' }, neo_sannyas: false,
  requested_authoring: false,
};

const MyTeacherProfile = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | teacher | null

  const fetchMine = async () => {
    setLoading(true);
    try { const r = await api.get('/teachers/mine'); setItems(r.data || []); }
    catch (e) { console.error('Failed to load teacher applications:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchMine(); }, []);

  const remove = async (t) => {
    if (!window.confirm('Delete this application?')) return;
    try { await api.delete(`/teachers/mine/${t.id}`); toast({ title: 'Deleted' }); fetchMine(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  if (editing) return <Editor teacher={editing === 'new' ? null : editing} onDone={() => { setEditing(null); fetchMine(); }} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Community</div>
          <h1 className="font-serif-display text-4xl text-burgundy">Become a <span className="font-italic-display text-rose-italic">Facilitator</span></h1>
          <p className="mt-2 text-mahogany/80 max-w-xl">Apply to be listed in the public directory as a facilitator or teacher — and request permission to host your own events. An admin reviews every application before it goes live.</p>
        </div>
        <button onClick={() => setEditing('new')} className="pill-btn" data-testid="apply-teacher-btn"><Plus className="w-4 h-4" /> Apply</button>
      </div>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="my-teacher-empty">No application yet. Apply to join the directory!</div>
        : (
          <ul className="space-y-3" data-testid="my-teacher-list">
            {items.map((t) => (
              <li key={t.id} className="p-5 rounded-2xl border border-rose/50 bg-white/70 flex flex-wrap items-center justify-between gap-4" data-testid={`my-teacher-${t.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {t.photo_url && <img src={mediaUrl(t.photo_url)} alt="" className="w-14 h-14 rounded-full object-cover" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif-display text-xl text-burgundy">{t.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[t.status] || ''}`}>{t.status}</span>
                      {t.is_verified && <BadgeCheck className="w-4 h-4 text-green-600" />}
                    </div>
                    {t.role && <div className="text-xs text-mahogany/60 mt-0.5">{t.role}</div>}
                    {t.status === 'inactive' && t.rejection_reason && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid={`my-teacher-feedback-${t.id}`}>
                        <span className="font-medium">Admin feedback:</span> {t.rejection_reason}
                        <span className="block text-red-500/80 mt-0.5">Edit your application to address the feedback — saving resubmits it for review.</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {t.status === 'active' && t.is_published && <IconBtn onClick={() => window.open(`/teachers/${t.slug}`, '_blank')} title="Preview"><ExternalLink className="w-4 h-4" /></IconBtn>}
                  <IconBtn onClick={() => setEditing(t)} title="Edit"><Pencil className="w-4 h-4" /></IconBtn>
                  <IconBtn onClick={() => remove(t)} title="Delete" danger><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

const Editor = ({ teacher, onDone, onCancel }) => {
  const { toast } = useToast();
  const [f, setF] = useState(teacher ? { ...BLANK, ...teacher, social: { ...BLANK.social, ...(teacher.social || {}) }, years_experience: teacher.years_experience ?? '' } : BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSoc = (k, v) => setF((p) => ({ ...p, social: { ...p.social, [k]: v } }));

  const save = async () => {
    if (!f.name) return toast({ title: 'Name is required' });
    setSaving(true);
    const payload = {
      name: f.name, role: f.role, photo_url: f.photo_url, short_bio: f.short_bio, full_bio: f.full_bio,
      specialties: Array.isArray(f.specialties) ? f.specialties : [],
      years_experience: f.years_experience ? parseInt(f.years_experience, 10) : null,
      gender: f.gender, experience_level: f.experience_level, availability: f.availability,
      social: f.social, neo_sannyas: f.neo_sannyas, requested_authoring: !!f.requested_authoring,
    };
    try {
      if (teacher) await api.patch(`/teachers/mine/${teacher.id}`, payload);
      else await api.post('/teachers/apply', payload);
      toast({ title: teacher ? 'Resubmitted for review' : 'Application submitted for review' });
      onDone();
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail }); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="teacher-application-editor">
      <div className="flex items-center justify-between"><h1 className="font-serif-display text-3xl text-burgundy">{teacher ? 'Edit Application' : 'Apply as Teacher / Facilitator'}</h1><button onClick={onCancel}><X className="w-5 h-5 text-mahogany/70" /></button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Txt label="Name *" value={f.name} onChange={(e) => set('name', e.target.value)} testid="apply-name-input" />
        <Txt label="Role / Specialty headline" value={f.role} onChange={(e) => set('role', e.target.value)} />
      </div>
      <ImageField label="Photo" url={f.photo_url} onUpload={async (file) => { try { const u = await uploadImage(file); set('photo_url', u); } catch (e) { console.error('Upload failed:', e); toast({ title: 'Upload failed', description: errText(e, 'Please try a different image.') }); } }} onClear={() => set('photo_url', '')} />
      <Txt label="Short Bio" value={f.short_bio} onChange={(e) => set('short_bio', e.target.value)} />
      <Area label="Full Bio" value={f.full_bio} onChange={(e) => set('full_bio', e.target.value)} />
      <Txt label="Specialties (comma separated)" value={(f.specialties || []).join(', ')} onChange={(e) => set('specialties', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
      <div className="grid sm:grid-cols-3 gap-3">
        <Txt label="Years Exp." type="number" value={f.years_experience} onChange={(e) => set('years_experience', e.target.value)} />
        <Sel label="Gender" value={f.gender} onChange={(e) => set('gender', e.target.value)} options={[['male', 'Male'], ['female', 'Female'], ['other', 'Other']]} />
        <Sel label="Availability" value={f.availability} onChange={(e) => set('availability', e.target.value)} options={[['full_time', 'Full Time'], ['part_time', 'Part Time']]} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Txt label="Facebook" value={f.social.facebook} onChange={(e) => setSoc('facebook', e.target.value)} />
        <Txt label="Instagram" value={f.social.instagram} onChange={(e) => setSoc('instagram', e.target.value)} />
        <Txt label="LinkedIn" value={f.social.linkedin} onChange={(e) => setSoc('linkedin', e.target.value)} />
        <Txt label="Website" value={f.social.website} onChange={(e) => setSoc('website', e.target.value)} />
      </div>
      <Chk label="Neo-Sannyas initiate" checked={f.neo_sannyas} onChange={(v) => set('neo_sannyas', v)} />
      <div className="p-4 rounded-2xl bg-rose-soft/25 border border-rose/40">
        <Chk label="I'd like to host / lead my own events" checked={f.requested_authoring} onChange={(v) => set('requested_authoring', v)} testid="request-authoring-chk" />
        <p className="mt-1.5 text-[11px] text-mahogany/60">An admin will review this and enable event creation for your account once approved.</p>
      </div>
      <div className="flex gap-3 pt-2"><button onClick={save} disabled={saving} className="pill-btn flex-1 justify-center" data-testid="apply-save-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {teacher ? 'Resubmit' : 'Submit Application'}</button><button onClick={onCancel} className="pill-btn-outline">Cancel</button></div>
    </div>
  );
};

const Txt = ({ label, testid, ...p }) => (<label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span><input {...p} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>);
const Area = ({ label, ...p }) => (<label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span><textarea rows={4} {...p} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>);
const Sel = ({ label, options, ...p }) => (<label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span><select {...p} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>);
const Chk = ({ label, checked, onChange, testid }) => (<label className="flex items-center gap-2 text-sm text-burgundy"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} /> {label}</label>);
const ImageField = ({ label, url, onUpload, onClear }) => (<div><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span><div className="flex items-center gap-2">{url ? <img src={mediaUrl(url)} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-rose-soft/60 flex items-center justify-center"><Upload className="w-4 h-4 text-mahogany/60" /></div>}<label className="text-xs pill-btn-outline cursor-pointer py-1.5">Upload<input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} /></label>{url && <button onClick={onClear} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}</div></div>);
const IconBtn = ({ children, danger, ...p }) => (<button {...p} className={`w-8 h-8 rounded-full flex items-center justify-center ${danger ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-rose-soft/60 text-mahogany hover:bg-rose-soft'}`}>{children}</button>);

export default MyTeacherProfile;
